import React, { useContext, useEffect, useRef, useState } from "react";
import { IMarker } from "./GeoMap";
import { MapContext } from "./MapContext";
import type { MarkerClusterer as MarkerClustererLibType } from "@googlemaps/markerclusterer";
import {
  Cluster,
  MarkerClusterer as MarkerClustererLib
} from "@googlemaps/markerclusterer";
import { IMapIcon, IMapSymbol } from "./MapIcons";

interface IMarkerClusterer {
  markers: IMarker[];
  highlightedMarkers?: IMarker[];
  onMarkerClick?: (marker: IMarker) => void;
  onClusterClick?: (markers: IMarker[]) => void;
  getMarkerIcon: (marker: IMarker) => IMapIcon | IMapSymbol;
  getClusterIcon: (markers: IMarker[]) => IMapIcon | IMapSymbol;
}

interface IMapMarkerWithData extends google.maps.Marker {
  __markerData?: IMarker;
}

const markersAreNotEqual = (markerA: IMarker, markerB: IMarker) => {
  return (
    markerA.title !== markerB.title ||
    markerA.position?.lat !== markerB.position?.lat ||
    markerA.position?.lng !== markerB.position?.lng ||
    markerA.selected !== markerB.selected ||
    markerA.active !== markerB.active
  );
};

export const MarkerClusterer: React.FC<IMarkerClusterer> = ({
  markers,
  onMarkerClick,
  onClusterClick,
  getMarkerIcon,
  getClusterIcon
}) => {
  const map = useContext(MapContext);
  const [markerClustererApi, setMarkerClustererApi] =
    useState<MarkerClustererLibType | null>(null);
  const onMarkerClickRef = useRef(onMarkerClick);
  const onClusterClickRef = useRef(onClusterClick);

  const getMarkerIconRef = useRef(getMarkerIcon);
  const getClusterIconRef = useRef(getClusterIcon);

  const mapMarkersStorageRef = useRef<Map<string, IMapMarkerWithData>>(
    new Map()
  );

  useEffect(() => {
    if (!map) {
      return;
    }

    const markerClusterer = new MarkerClustererLib({
      map,
      markers: [],
      onClusterClick: (event, cluster) => {
        if (onClusterClickRef.current) {
          onClusterClickRef.current(
            cluster?.markers?.map(
              (m: IMapMarkerWithData) => m.__markerData as IMarker
            ) ?? []
          );
        }
      },
      renderer: {
        render(cluster: Cluster): google.maps.Marker {
          const icon = getClusterIconRef.current(
            cluster.markers?.map((m: IMapMarkerWithData) => {
              return m.__markerData!;
            }) ?? []
          );
          return new google.maps.Marker({
            position: cluster.position,
            icon: icon,
            label: {
              text: String(cluster.markers?.length),
              color: icon.labelColor,
              className: "MarkerClusterer__MarkerLabel"
            }
          });
        }
      }
    });

    setMarkerClustererApi(markerClusterer);

    return () => {
      // markerClusterer.clearMarkers();
    };
  }, [map]);

  useEffect(() => {
    if (!markerClustererApi) {
      return;
    }

    const markersToCreate: IMarker[] = [];
    const markersToUpdate: IMarker[] = [];
    const markersToDelete: google.maps.Marker[] = [];

    const mapMarkersStorage = mapMarkersStorageRef.current;

    const existedMarkers: { [key: string]: boolean } = {};

    markers.forEach((newMarker) => {
      existedMarkers[newMarker.id] = true;
      const mapMarkerFromStorage = mapMarkersStorage.get(newMarker.id);
      if (!mapMarkerFromStorage) {
        markersToCreate.push(newMarker);
      } else {
        if (
          markersAreNotEqual(
            mapMarkerFromStorage.__markerData as IMarker,
            newMarker
          )
        ) {
          markersToUpdate.push(newMarker);
        }
      }
    });
    // console.log("currentMarkers", currentMarkers);
    // console.log("markersToCreate", markersToCreate);
    // console.log("markersToUpdate", markersToUpdate);
    //
    // delete not existed markers
    mapMarkersStorage.forEach((_, key) => {
      if (!existedMarkers[key]) {
        const mapMarker = mapMarkersStorage.get(key);
        if (mapMarker) {
          mapMarkersStorage.delete(key);
          markersToDelete.push(mapMarker);
        }
      }
    });

    //
    // markersToUpdate.forEach((m) => {
    //   const mapMarker = mapMarkersStorage.get(m.id);
    //   if (mapMarker) {
    //     // mapMarker.setMap(null);
    //     mapMarkersStorage.delete(m.id);
    //     markersToDelete.push(mapMarker);
    //   }
    // });

    // update existed markers
    markersToUpdate.forEach((m) => {
      // console.log("update marker", m.title, m);
      const mapMarker = mapMarkersStorage.get(m.id)!;
      // @ts-ignore
      // console.log(
      //   "marker index",
      //   // @ts-ignore
      //   [...mapMarkersStorage.values()].findIndex(
      //     (mm) => mm.__markerData?.id === mapMarker.__markerData?.id
      //   )
      // );

      if (mapMarker) {
        // console.log("mapMarker.setIcon:mapMarker:before", {
        //   // @ts-ignore
        //   ...mapMarker.getIcon()
        // });
        const icon = getMarkerIconRef.current(m);
        // console.log("mapMarker.setIcon:newIcon", icon);
        mapMarker.setIcon(icon);
        mapMarker.setPosition(m.position);
        mapMarker.setTitle(m.title);
        mapMarker.setLabel({
          text: "1",
          color: icon.labelColor,
          className: "MarkerClusterer__MarkerLabel"
        });
        mapMarker.__markerData = m;
        // console.log("mapMarker.setIcon:mapMarker:after", {
        //   // @ts-ignore
        //   ...mapMarker.getIcon()
        // });
      }
    });

    // create new markers
    const newMapMarkers = markersToCreate.map((m) => {
      const icon = getMarkerIconRef.current(m);
      const mapMarker = new google.maps.Marker({
        position: m.position,
        icon: icon,
        title: m.title,
        label: {
          text: "1",
          color: icon.labelColor,
          className: "MarkerClusterer__MarkerLabel"
        }
      }) as IMapMarkerWithData;
      mapMarker.__markerData = m;

      if (onMarkerClickRef.current) {
        mapMarker.addListener("click", () => {
          if (onMarkerClickRef.current) {
            onMarkerClickRef.current(m);
          }
        });
      }

      return mapMarker;
    });

    newMapMarkers.forEach((mapMarker) => {
      mapMarkersStorage.set(mapMarker.__markerData?.id!, mapMarker);
    });

    let rerenderWillBeCalled = false;

    if (markersToDelete.length > 0) {
      rerenderWillBeCalled = true;
      markerClustererApi.removeMarkers(markersToDelete);
    }
    if (newMapMarkers.length > 0) {
      rerenderWillBeCalled = true;
      markerClustererApi.addMarkers(newMapMarkers);
    }

    // update clusters markers
    if (!rerenderWillBeCalled) {
      // @ts-ignore access to protected property
      markerClustererApi.clusters.forEach((cluster) => {
        if (cluster.markers?.length && cluster.markers?.length > 1) {
          const icon = getClusterIconRef.current(
            cluster.markers?.map((m: IMapMarkerWithData) => {
              return m.__markerData!;
            }) ?? []
          );
          cluster.marker.setIcon(icon);
          cluster.marker.setLabel({
            text: String(cluster.markers?.length),
            color: icon.labelColor,
            className: "MarkerClusterer__MarkerLabel"
          });
        }
      });
    }

    return () => {
      // markerClustererApi.removeMarkers(newMapMarkers);
    };
  }, [markers, markerClustererApi]);

  return null;
};
